"use client";

import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import thankYouService from "@/services/thank-you-service";
import { clearSessionCache } from "@/services/axios";
import { useAuth } from "@/hooks/useAuth";
import LoginRegistrationModal from "./LoginRegistrationModal";
import { defaultCardDates, SYMBOL_IMAGES } from "@/app/components/appcomponents/thankYouCard";
import StepMessage, { isStepMessageValid } from "./thankYouWizard/StepMessage";
import StepCard from "./thankYouWizard/StepCard";
import StepCandle from "./thankYouWizard/StepCandle";
import Preview from "./thankYouWizard/Preview";

const EMPTY_FORM = {
  // step 1
  obituary: null,
  search: "",
  headline: "",
  message: "",
  authorName: "",
  alignment: "left",
  background: "cream",
  // step 2: what the author changes on the card above the message
  cardForId: null,
  cardDates: "",
  cardLocation: "",
  cardSymbol: "",
  cardPhotoFile: null,
  cardPhotoPreview: null,
  // step 3
  candle: "",
};

/**
 * "Add a message" popup (Zahvala or Spomin; `kind` picks the Stripe product):
 * 1. message, 2. finish the card above it (with a preview), 3. optional candle, then Stripe to pay.
 * The message is published once the payment is confirmed.
 */
export default function AddThankYouModal({ onClose, obituary = null, kind = "zahvala" }) {
  const [step, setStep] = useState(1);
  // With a memory page given (from a memory page or from Step 0) the popup starts with it chosen.
  const [form, setForm] = useState(() => (obituary ? { ...EMPTY_FORM, obituary } : EMPTY_FORM));
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The message is paid for with an account: a visitor who is not logged in is asked at the end (the popup and
  // everything typed stay as they are), and the order goes on right after the login.
  const { isAuthenticated } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);

  const update = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showPreview) setShowPreview(false);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, showPreview]);

  // Free the temporary address of the chosen photo when the popup goes away.
  useEffect(() => () => form.cardPhotoPreview && URL.revokeObjectURL(form.cardPhotoPreview), [form.cardPhotoPreview]);

  const goToCard = (current = form) => {
    if (!isStepMessageValid(current)) return;
    const { obituary } = current;
    if (current.obituary !== form.obituary) update({ obituary });
    // Start from what the obituary has, unless the author already edited the card for this person.
    if (current.cardForId !== obituary.id) {
      update({
        cardForId: obituary.id,
        cardDates: defaultCardDates(obituary),
        cardLocation: obituary.location ?? "",
        cardSymbol: SYMBOL_IMAGES[String(obituary.symbol)] ? String(obituary.symbol) : "",
        cardPhotoFile: null,
        cardPhotoPreview: null,
      });
    }
    setStep(2);
  };

  // The dates field is prefilled (as in the design). Only what the author really changed goes on the
  // card; untouched, the card keeps its standard dates.
  const editedDates = (current) =>
    current.cardDates.trim() !== defaultCardDates(current.obituary) ? current.cardDates.trim() : undefined;

  const submit = async (skipLoginCheck = false) => {
    if (submitting) return;
    if (!skipLoginCheck && !isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      let cardImage;
      if (form.cardPhotoFile) {
        ({ url: cardImage } = await thankYouService.uploadThankYouCardImage(form.cardPhotoFile));
      }

      const { paymentUrl } = await thankYouService.createThankYouMessage({
        obituaryId: form.obituary.id,
        headline: form.headline.trim(),
        message: form.message.trim(),
        authorName: form.authorName.trim(),
        alignment: form.alignment,
        background: form.background,
        kind,
        ...(editedDates(form) !== undefined ? { cardDates: editedDates(form) } : {}),
        cardLocation: form.cardLocation.trim(),
        cardSymbol: form.cardSymbol,
        ...(cardImage ? { cardImage } : {}),
        ...(form.candle ? { candle: form.candle } : {}),
      });

      if (!paymentUrl) {
        throw new Error("Napaka pri pripravi plačila. Poskusite znova.");
      }

      window.location.href = paymentUrl;
    } catch (error) {
      setSubmitting(false);
      toast.error(
        error?.response?.data?.message || error?.message || "Sporočila ni bilo mogoče shraniti"
      );
    }
  };

  const previewMessage = {
    id: "preview",
    headline: form.headline.trim(),
    message: form.message.trim(),
    authorName: form.authorName.trim(),
    alignment: form.alignment,
    background: form.background,
    cardDates: editedDates(form),
    cardLocation: form.cardLocation,
    cardImage: form.cardPhotoPreview || undefined,
    cardSymbol: form.cardSymbol,
    Obituary: form.obituary,
  };

  return (
    <>
      {step === 1 && (
        <StepMessage
          form={form}
          update={update}
          onNext={goToCard}
          onClose={onClose}
          obituaryLocked={Boolean(obituary)}
        />
      )}
      {step === 2 && (
        <StepCard
          form={form}
          update={update}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
          onPreview={() => setShowPreview(true)}
          onClose={onClose}
        />
      )}
      {step === 3 && (
        <StepCandle
          form={form}
          update={update}
          onBack={() => setStep(2)}
          onSubmit={() => submit()}
          submitting={submitting}
          onClose={onClose}
        />
      )}
      {showPreview && <Preview message={previewMessage} onClose={() => setShowPreview(false)} />}
      <LoginRegistrationModal
        isOpen={loginOpen}
        zIndex={10002}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={() => {
          setLoginOpen(false);
          clearSessionCache();
          submit(true);
        }}
        initialTab="login"
      />
    </>
  );
}
